import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { StickyButton } from "../../StickyButton";
import { useStickyTourStore } from "../../../../stores/stickyTourStore";
import { FeedbackTourVariations, TourSignal } from "../../TourTypes";
import { useCommentStore } from "../../../../stores/commentStore";
import { countWords } from "../../../../Utils";
import Crossroads from "../../Crossroads";
import { SAMPLE_CHAPTER_HTML } from "../../fixtures/sampleChapter";

const MIN_FEEDBACK_WORDS = 500;

export const FeedbackTourIntroContent = () => {
  const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);

  return (
    <>
      <p>Get a second set of eyes on your work with <strong>Feedback</strong>.</p>
      <StickyButton
        text="Next"
        variant="go-button"
        size="small"
        onClick={() => sendFlowSignal(TourSignal.STICKY_NEXT)}
      />
    </>
  );
};

export const FeedbackTourPasteContent = () => {
  const tiptapEditor = useCommentStore((state) => state.tiptapEditor);
  const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);
  const [wordCount, setWordCount] = useState(0);
  const hasAutoAdvancedRef = useRef(false);

  useEffect(() => {
    if (!tiptapEditor) return;

    const updateCount = () => {
      setWordCount(countWords(tiptapEditor.state.doc.textContent));
    };

    updateCount();
    tiptapEditor.on("update", updateCount);
    return () => {
      tiptapEditor.off("update", updateCount);
    };
  }, [tiptapEditor]);

  useEffect(() => {
    if (wordCount < MIN_FEEDBACK_WORDS) {
      hasAutoAdvancedRef.current = false;
      return;
    }

    if (!hasAutoAdvancedRef.current) {
      hasAutoAdvancedRef.current = true;
      sendFlowSignal(TourSignal.STICKY_NEXT);
    }
  }, [wordCount, sendFlowSignal]);

  const handleSkip = useCallback(() => {
    if (tiptapEditor) {
      tiptapEditor.commands.insertContent(SAMPLE_CHAPTER_HTML);
    }
    sendFlowSignal(TourSignal.STICKY_NEXT);
  }, [tiptapEditor, sendFlowSignal]);

  const canContinue = wordCount >= MIN_FEEDBACK_WORDS;
  const wordsRemaining = MIN_FEEDBACK_WORDS - wordCount;

  if (canContinue) return null;

  return (
    <>
      {wordCount > 0 ? (
        <p>
          Add <strong>{wordsRemaining}</strong> more {wordsRemaining === 1 ? "word" : "words"} to continue.
        </p>
      ) : (
        <p>Paste a snippet of your work (at least 500 words) to get started.</p>
      )}
      <StickyButton text="Skip" variant="no-button" size="small" onClick={handleSkip} />
    </>
  );
};

export const FeedbackTourChooseTypeContent = () => {
  const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);

  return (
    <>
      <p>Choose the type of <strong>Feedback</strong> you'd like.</p>
      <StickyButton
        text="Done"
        variant="go-button"
        size="small"
        onClick={() => sendFlowSignal(TourSignal.STICKY_NEXT)}
      />
    </>
  );
};

export const FeedbackTourRunFeedbackContent = () => (
  <p>Great! Click on <strong>Feedback</strong> to get comments on your document.</p>
);

export const FeedbackTourPrefilledContent = () => {
  const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);

  return (
    <>
      <p>We've prefilled this document with some of your work so you can test it out while we finish your import.</p>
      <StickyButton
        text="Let's do this"
        variant="go-button"
        size="small"
        onClick={() => sendFlowSignal(TourSignal.STICKY_NEXT)}
        className="mb-[-20px] ml-[75px]"
      />
    </>
  );
};

export const FeedbackTourWrapUpContent = () => {
  const history = useHistory();
  const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);
  const variation = useStickyTourStore((state) => state.activeTourVariation) as FeedbackTourVariations | undefined;

  const isNotImported = variation === FeedbackTourVariations.NOT_IMPORTED;

  const handleImportProject = useCallback(() => {
    sendFlowSignal(TourSignal.STICKY_NEXT);
    history.push("/app?openImport=1");
  }, [history, sendFlowSignal]);

  const handleMaybeLater = useCallback(() => {
    sendFlowSignal(TourSignal.STICKY_NEXT);
  }, [sendFlowSignal]);

  const message = isNotImported
    ? "Have fun! Hot tip: Sudowrite also gives you a custom report when you import your work!"
    : "Have fun and we'll let you know when your import finishes!";

  return (
    <>
      <p className="mt-[30px]">{message}</p>
      {isNotImported ? (
        <div className="mt-[100px] ml-[30px] relative">
          <Crossroads
            goText="Import"
            noText="Maybe later"
            goOnClick={handleImportProject}
            noOnClick={handleMaybeLater}
          />
        </div>
      ) : (
        <StickyButton
          text="Done"
          variant="go-button"
          onClick={() => sendFlowSignal(TourSignal.STICKY_NEXT)}
        />
      )}
    </>
  );
};
